<template>
  <div>
    <NuxtLayout
      name="asuostaticlayout"
      :title="$page?.studentsCommunity?.title"
      :image-source="$page?.studentsCommunity?.heroImage"
      static-rfi-bg-color="#fff"
    >
      <!-- Section Intro -->
      <SectionApollo
        :title="$page?.studentsCommunity?.introTitle!"
        :image="$page?.studentsCommunity?.introImage!"
        :style="getBgImage"
      >
        <div v-html="$page?.studentsCommunity?.introDescription"></div>
      </SectionApollo>

      <!-- students section -->
      <section class="py-space-md py-md-space-lg py-lg-space-xxl">
        <div class="container px-lg-0">
          <div class="row">
            <div class="col-12 col-lg-8">
              <h2 class="h2-medium pb-space-sm">
                {{ $page?.studentsCommunity?.studentTitle }}
              </h2>
              <div
                class="fs-medium pb-space-md pb-lg-space-lg"
                v-html="$page?.studentsCommunity?.studentDescription"
              ></div>
            </div>
          </div>
          <div class="row row-cols-1 row-cols-md-2 row-cols-lg-4">
            <div
              v-for="(card, index) in $page?.studentsCommunity?.studentCards"
              :key="index"
              class="col mb-space-sm mb-lg-0"
            >
              <CardInfoVertical
                class="h-100"
                :title="card?.title!"
                title-size="medium"
                :display-title-highlight="false"
                :image-source="card?.image!"
                :cta-as-button="true"
                cta-button-variant="secondary"
                cta-text-variant="dark-3"
                cta-text-size="medium"
                cta-text-weight="bold"
                :cta-text="card?.link?.text!"
                :cta-link="card?.link?.uri!"
                cta-link-target="_blank"
                @civ-cta-click="
                  handleLinkEvent(
                    $event,
                    card?.title!,
                    card?.link?.text!,
                    card?.link?.uri!,
                    $page?.studentsCommunity?.studentTitle!
                  )
                "
              >
                <div class="mb-space-sm" v-html="card?.text"></div>
              </CardInfoVertical>
            </div>
          </div>
        </div>
      </section>

      <!-- tradition section -->
      <SectionalTestimonialVideo
        class="mb-lg-space-xxl"
        :title="$page?.studentsCommunity?.traditionTitle!"
        title-level="h2"
        :description="$page?.studentsCommunity?.traditionDescription!"
        :items="traditionItems"
        :model-zindex="1055"
        :display-cta="false"
        @card-clicked="handleTraditionCardClick"
      />

      <!-- alumni-association section -->
      <div class="container py-space-xl">
        <div class="row gx-0">
          <div class="col-12 offset-lg-1 col-lg-10">
            <CardInfoHorizontal
              :bg-image-source="($page?.studentsCommunity?.connectionImage || undefined) as string"
              :image-fit="$page?.studentsCommunity?.connectionImage ? 'cover' : 'contain'"
              :bg-image-alt="$page?.studentsCommunity?.connectionImageAlt!"
              image-position="left"
              :display-label="false"
              :title="$page?.studentsCommunity?.connectionTitle!"
              title-size="large"
              :highlight-width="8"
              :cta-as-button="true"
              cta-button-variant="secondary"
              cta-text-variant="dark-3"
              cta-text-size="medium"
              cta-text-weight="bold"
              :cta-text="$page?.studentsCommunity?.connectionLink?.text!"
              :cta-link="$page?.studentsCommunity?.connectionLink?.uri!"
              :cta-link-target="handleCtaTarget($page?.studentsCommunity?.connectionLink?.target!)"
              @cta-click="
                handleCtaClick(
                  $event,
                  $page?.studentsCommunity?.connectionTitle!,
                  $page?.studentsCommunity?.connectionLink! as unknown as ParagraphHtmlLink,
                  $page?.studentsCommunity?.connectionTitle!
                )
              "
            >
              <div
                class="mb-space-sm"
                v-html="$page?.studentsCommunity?.connectionDescription"
              ></div>
            </CardInfoHorizontal>
          </div>
        </div>
      </div>
      <!-- explore-your-future section -->
      <SectionExploreYourFuture
        :title="$page?.studentsCommunity?.exploreTitle!"
        :items="$page?.studentsCommunity?.exploreCards! as ExploreItem[]"
        :bg-image="$page?.studentsCommunity?.exploreBg!"
      />
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import type { ExploreItem } from "~/components/students/SectionExploreYourFuture.vue";
import SectionExploreYourFuture from "~/components/students/SectionExploreYourFuture.vue";
import { useUtilityService } from "~/composables/useUtilityService";
import type { ParagraphHtmlLink, StudentsCommunity } from "~/server/resolvers/resolvers-types";
import type { StudentsCommunityQuery } from "~/types/graphql-operations";

const UtilityService = useUtilityService();
const config = useRuntimeConfig();

definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"]
  }
});

const $page = await usePageQuery<StudentsCommunityQuery>(
  /* GraphQL */ `
    #graphql
    query studentsCommunity($studentsCommunityId: ID!) {
      studentsCommunity(id: $studentsCommunityId) {
        detailPage
        metaTitle
        metaDescription
        heroImage
        title
        introTitle
        introImage
        introBg
        introDescription
        studentTitle
        studentDescription
        studentCards {
          title
          image
          text
          link {
            uri
            text
            target
          }
        }
        traditionTitle
        traditionImage
        traditionDescription
        traditionCards {
          title
          image
          imageAlt
          icon
          text
        }
        connectionTitle
        connectionImage
        connectionImageAlt
        connectionDescription
        connectionLink {
          uri
          text
          target
        }
        exploreTitle
        exploreBg
        exploreCards {
          title
          image
          link {
            uri
            target
          }
        }
      }
    }
  `,
  {
    studentsCommunityId: "950617e8-c146-40e2-bfb2-baa8e65941d5"
  },
  {
    key: "students-community"
  }
);

useHead({
  title: $page?.studentsCommunity?.metaTitle,
  meta: [
    { name: "Title", content: $page?.studentsCommunity?.metaTitle },
    {
      name: "Description",
      content: $page?.studentsCommunity?.metaDescription
    }
  ],
  link: [
    {
      rel: "canonical",
      href: UtilityService.formatCanonicalUrl(
        `${config.public.siteUrl}${$page?.studentsCommunity?.detailPage}`
      )
    }
  ],
  script: [
    useSchemaService().generateStudentsCommunitySchema(
      $page?.studentsCommunity as unknown as StudentsCommunity
    )
  ]
});

const getBgImage = computed(() => {
  return {
    "background-image": `url('${$page?.studentsCommunity?.introBg}')`
  };
});

const handleCtaTarget = (target: string) => {
  return target === "BLNK" ? "_blank" : "_self";
};

const traditionItems = computed(() =>
  ($page?.studentsCommunity?.traditionCards ?? []).map((card) => ({
    imageSrc: card?.image ?? "",
    mediaSrc: card?.image ?? "",
    previewSrc: card?.image ?? "",
    imageAlt: card?.imageAlt ?? "",
    mediaType: "image",
    name: card?.title ?? "",
    role: "",
    department: "",
    description: card?.text ?? ""
  }))
);

const handleTraditionCardClick = (id: number) => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "open",
    "click",
    "main content",
    $page?.studentsCommunity?.traditionTitle?.toLowerCase() ?? "",
    $page?.studentsCommunity?.traditionCards?.[id]?.title?.toLowerCase() ?? ""
  );
};

const handleCtaClick = (
  event: Event,
  title: string,
  link: ParagraphHtmlLink,
  component: string
) => {
  event.preventDefault();

  if (link && link.text) {
    analyticsComposable.trackLinkEvent(
      "internal link",
      "main content",
      title.toLowerCase(),
      link.text.toLowerCase(),
      component.toLowerCase()
    );
  } else {
    console.error("missing link or link text");
  }
  navigateTo(link.uri, {
    open: {
      target: handleCtaTarget(link.target as string)
    }
  });
};

const handleLinkEvent = (
  event: Event,
  section: string,
  text: string,
  uri: string,
  component?: string
) => {
  event.preventDefault();
  const targetHref = (event.target as HTMLAnchorElement)?.getAttribute("href");
  const isInternalLink = targetHref && targetHref.includes("asu.edu");
  const linkType = isInternalLink ? "internal link" : "external link";
  analyticsComposable.trackLinkEvent(
    linkType,
    "main content",
    section.toLowerCase(),
    text.toLowerCase(),
    component ? component.toLowerCase() : undefined
  );
  navigateTo(uri, {
    open: {
      target: "_blank"
    }
  });
};
</script>
